import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Loader } from 'common/react/components/Loader';
import { StatComparisonRow } from './StatComparisonRow';
import {
    statComparisonDataTypeSelector,
    statComparisonNavSelector
} from 'store/modules/statspro/stat-comparison/selectors';
import { composeData, composeRows } from '../utils';

import {
    useGetPlayerCareerSeasonStatsQuery,
    useGetPlayerProfileQuery
} from 'store/modules/api/statsPro-api';

export const StatComparisonMain = (props) => {
    const nav = useSelector(statComparisonNavSelector);
    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    const season = seasons.find((item) => item.id === nav.season.id);
    const seasonId = season?.providerId ?? props.seasonId;
    const dataType = useSelector(statComparisonDataTypeSelector);

    const [composedPlayersData, setComposedPlayersData] = useState(null);

    const { data: playerOneCareerData } = useGetPlayerProfileQuery(
        { seasonId, playerId: props.playerIds[1] },
        { skip: !props.playerIds[1] }
    );
    const { data: playerTwoCareerData } = useGetPlayerProfileQuery(
        { seasonId, playerId: props.playerIds[2] },
        { skip: !props.playerIds[2] }
    );

    const { data: playerOneSeasonData } = useGetPlayerCareerSeasonStatsQuery(
        props.playerIds[1],
        { skip: !props.playerIds[1] }
    );
    const { data: playerTwoSeasonData } = useGetPlayerCareerSeasonStatsQuery(
        props.playerIds[2],
        { skip: !props.playerIds[2] }
    );

    /**
     * Get rows for the selected category
     */
    const rows = useMemo(
        () => composeRows(nav.season.id, nav.category),
        [nav.category]
    );

    // Update the data when the nav changes
    useEffect(() => {
        if (nav.season.id === -1) {
            // career stats
            setComposedPlayersData([
                composeData(rows, playerOneCareerData, dataType.type),
                composeData(rows, playerTwoCareerData, dataType.type)
            ]);
        } else {
            // season stats
            setComposedPlayersData([
                composeData(
                    rows,
                    playerOneSeasonData?.yearlySeasonStats?.find(
                        (item) => item.seasonId === seasonId
                    ),
                    dataType.type
                ),
                composeData(
                    rows,
                    playerTwoSeasonData?.yearlySeasonStats?.find(
                        (item) => item.seasonId === seasonId
                    ),
                    dataType.type
                )
            ]);
        }
    }, [
        nav.season.id,
        rows,
        playerOneCareerData,
        playerTwoCareerData,
        playerOneSeasonData,
        playerTwoSeasonData,
        dataType.type
    ]);

    // Loader and empty state for data
    if (!composedPlayersData) {
        return (
            <div className="stat-comparison__loader">
                <Loader />
            </div>
        );
    }

    return (
        <div className="stat-comparison__stats-wrapper">
            {rows.map((row, i) => (
                <StatComparisonRow
                    key={i}
                    statName={row.name}
                    data={[
                        composedPlayersData[0].find(
                            (item) => item.name === row.name
                        ),
                        composedPlayersData[1].find(
                            (item) => item.name === row.name
                        )
                    ]}
                />
            ))}
        </div>
    );
};

StatComparisonMain.propTypes = {
    playerIds: PropTypes.object.isRequired,
    seasonId: PropTypes.string.isRequired
};
